import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { fetchCountsService } from "../../../services/fetchCountsService";
import { strategyEngineService } from "../../../services/strategyEngineService";
import { usePermission } from "../../../hooks/usePermissions";

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  // Live "signals today" count for the Strategy Engine nav badge. Defaults
  // to 0 (no badge shown) and only ever updates on a successful fetch, so a
  // strategy-engine outage can never affect the navbar.
  const [signalsToday, setSignalsToday] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navRef = useRef(null);

  const { hasPermission } = usePermission()

  const timeoutRef = useRef(null);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const countResponse = await fetchCountsService.getCounts();
        setCount(countResponse);
      } catch (error) {
        console.error("Error fetching counts:", error);
      }
    };

    const handleClickOutside = (event) => {
      // Handle profile dropdown
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      // Handle nav dropdowns
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    fetchCounts();
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ── "Signals today" badge ──────────────────────────────────────────
  // Fetches the count from the Strategy Engine (:3003) on mount and then
  // refreshes every 60s. Fully isolated and defensive: any error (engine
  // down, not configured, 401) simply leaves the count at its last good
  // value — it never throws into the navbar render. The `active` flag
  // guards against a state update after unmount.
  useEffect(() => {
    let active = true;

    const fetchSignalsToday = async () => {
      try {
        const res = await strategyEngineService.getSignalsCountToday();
        const c = res?.data?.count;
        if (active && Number.isFinite(c)) setSignalsToday(c);
        // Same call tells us if the viewer is an admin → gates the
        // "My / All Strategies" submenu below. Defensive: only update on a boolean.
        if (active && typeof res?.data?.isAdmin === 'boolean') setIsAdmin(res.data.isAdmin);
      } catch (err) {
        // Swallow — a badge must never break the navigation.
      }
    };

    fetchSignalsToday();
    const interval = setInterval(fetchSignalsToday, 60000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [hasPermission]);

  const handleSignOut = () => {
    localStorage.clear();
    localStorage.removeItem('isLoggedIn');
    window.location.href = '/';
  };

  const menuItems = [
    { path: "/", icon: <span className="material-symbols-outlined">home</span>, label: "Home", permissions: "VIEW_HOME" },
    // { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    {
      label: "Users",
      icon: <span className="material-symbols-outlined">groups</span>,
      children: [
        { path: "/users", icon: <span className="material-symbols-outlined">groups</span>, label: "Users List", permissions: "VIEW_USERSLIST" },
        { path: "/users/kyc", icon: <span className="material-symbols-outlined">groups</span>, label: "Users KYC", permissions: "VIEW_USERKYC" },
        { path: "/users/documents", icon: <span className="material-symbols-outlined">document_search</span>, label: "User Documents", permissions: "VIEW_USERDOCUMENT" },
        { path: "/users/details", icon: <span className="material-symbols-outlined">person_search</span>, label: "User Details", permissions: "VIEW_USERSLIST" },
        { path: "/users/compliance", icon: <span className="material-symbols-outlined">local_library</span>, label: "Research Analyst", permissions: "VIEW_RESEARCHANALYST" },
        { path: "/block-keyword", icon: <span className="material-symbols-outlined">block</span>, label: "Blocking UI", permissions: "VIEW_BLOCK_KEYWORD" },
        // { path: "/redflag", icon: "🚩", label: `RedFlags (${count?.totalOpenRedFlags || 0})` },
        { path: "/tickets", icon: <span className="material-symbols-outlined">report</span>, label: `Tickets (${count?.totalGrievance || 0})`, permissions: "VIEW_GRIEVANCE" }
      ]
    },

    {
      label: "Learning",
      icon: <span className="material-symbols-outlined">book_4</span>,
      children: [
        { path: "/Learning", icon: <span className="material-symbols-outlined">book_4</span>, label: "Learning Module Master", permissions: "VIEW_LEARNINGCONTENT" },
        { path: "/chaptermaster", icon: <span className="material-symbols-outlined">book_4</span>, label: "Chapter Master", permissions: "VIEW_CHAPTERMASTER" },
        { path: "/Learningmodule", icon: <span className="material-symbols-outlined">book_4</span>, label: "Learning Content", permissions: "VIEW_LEARNINGMODULEMASTER" }
      ]
    },
    { path: "/scans", icon: <span className="material-symbols-outlined">diversity_3</span>, label: "Scans", permissions: "VIEW_SCANS" },


    // { path: '/watchlist', icon: '⭐', label: 'Watchlist' },
    // { path: '/portfolio', icon: '💼', label: 'Portfolio' },
    {
      label: "Education",
      icon: <span className="material-symbols-outlined">
        auto_stories
      </span>,
      children: [
        { path: "/educationfeed", icon: <span className="material-symbols-outlined">newspaper</span>, label: "Education Post", permissions: "VIEW_EDUCATIONFEED" },
        { path: "/education_category", icon: <span className="material-symbols-outlined">category</span>, label: "Education Category", permissions: "VIEW_EDUCATIONCATEGORY" },
      ]
    },
    {
      label: "News",
      icon: <span className="material-symbols-outlined">newspaper</span>,
      children: [
        { path: "/newsfeed", icon: <span className="material-symbols-outlined">newspaper</span>, label: "News Feed", permissions: ["VIEW_NEWSFEED", "VIEW_CORPORATE_ANNOUNCEMENT"] },
        { path: "/news_category", icon: <span className="material-symbols-outlined">category</span>, label: "News Category", permissions: "VIEW_NEWSCATEGORY" },
      ]
    },
    {
      label: "Trading",
      icon: <span className="material-symbols-outlined">finance_mode</span>,
      children: [
        { path: "/stock-details", icon: <span className="material-symbols-outlined">query_stats</span>, label: "Stock Details", permissions: "VIEW_STOCK_DETAILS" },
        { path: "/internal-tokens", icon: <span className="material-symbols-outlined">token</span>, label: "Internal Tokens", permissions: "VIEW_INTERNALTOKEN_INDICESCONTROL" },
        { path: "/indices-control", icon: <span className="material-symbols-outlined">candlestick_chart</span>, label: "Indices Control", permissions: "VIEW_INTERNALTOKEN_INDICESCONTROL" },
        { path: "/traderecommendations", icon: <span className="material-symbols-outlined">thumb_up</span>, label: "Trade Recommendations", permissions: "VIEW_TRADERECOMMENDATIONS" },
        { path: "/control-tradingdays", icon: <span className="material-symbols-outlined">calendar_month</span>, label: "Control Trading Days", permissions: "VIEW_CONTROLTRADING" },
        { path: "/industry-master", icon: <span className="material-symbols-outlined">home_work</span>, label: "Industry Master", permissions: "VIEW_INDUSTRY_MASTER" },
        { path: "/sector-master", icon: <span className="material-symbols-outlined">home_work</span>, label: "Sector Master", permissions: "SECTOR_MASTER" },
        { path: "/investment-ipo", icon: <span className="material-symbols-outlined">trending_up</span>, label: "IPO", permissions: "VIEW_PRIMARY_MARKET" },
        { path: "/investment-ncd", icon: <span className="material-symbols-outlined">account_balance</span>, label: "NCD", permissions: "VIEW_PRIMARY_MARKET" },
        { path: "/investment-corporate-bond", icon: <span className="material-symbols-outlined">request_quote</span>, label: "Corporate Bond", permissions: "VIEW_PRIMARY_MARKET" },
        { path: "/investment-fixed-deposit", icon: <span className="material-symbols-outlined">savings</span>, label: "Fixed Deposit", permissions: "VIEW_PRIMARY_MARKET" },
      ]
    },
    {
      // Strategy Engine — dedicated top-level group for the trading
      // strategy/signal feature (served by the separate :3003 backend).
      // Pulled out of "Trading" so it has its own home and room to grow
      // (Backtests / Positions / Performance can slot in here later).
      label: "Strategies",
      icon: <span className="material-symbols-outlined">smart_toy</span>,
      // Badge on the group header mirrors the Signals child count, so the
      // "signals today" number is visible without opening the dropdown.
      badge: signalsToday,
      children: [
        { path: "/strategies", icon: <span className="material-symbols-outlined">neurology</span>, label: "Strategies", permissions: "VIEW_STRATEGYENGINE" },
        { path: "/signals", icon: <span className="material-symbols-outlined">notifications_active</span>, label: "Signals", badge: signalsToday, permissions: "VIEW_SIGNALS" },
      ]
    },
    {
      label: "Import",
      icon: <span className="material-symbols-outlined">upload_file</span>,
      permissions: "VIEW_MARKETDATA_IMPORT",
      children: [
        { path: "/market_import_data", icon: <span className="material-symbols-outlined">upload_file</span>, label: "Market Data Import" },
        { path: "/equity_market_cap", icon: <span className="material-symbols-outlined">upload_file</span>, label: "Equity Market Cap" },
        { path: "/deals_import", icon: <span className="material-symbols-outlined">upload_file</span>, label: "Deals Import" },
        { path: "/gross_deliverables", icon: <span className="material-symbols-outlined">upload_file</span>, label: "Gross Deliverables" },
        { path: "/fifty_two_week_high", icon: <span className="material-symbols-outlined">upload_file</span>, label: "52 Week High" },
        { path: "/fifty_two_week_low", icon: <span className="material-symbols-outlined">upload_file</span>, label: "52 Week Low" },
        { path: "/fii_data", icon: <span className="material-symbols-outlined">upload_file</span>, label: "FII Data" },
        { path: "/fii_fo_data", icon: <span className="material-symbols-outlined">upload_file</span>, label: "FII F&O Data" },
        { path: "/pe_ratio", icon: <span className="material-symbols-outlined">upload_file</span>, label: "P/E Ratio" },
      ]
    },
    {
      label: "Payments",
      icon: <span className="material-symbols-outlined">payments</span>,
      children: [
        { path: "/plans", icon: <span className="material-symbols-outlined">upcoming</span>, label: "Plans", permissions: "VIEW_PLANS" },
        { path: "/pro-plan-transactions", icon: <span className="material-symbols-outlined">star_shine</span>, label: "Pro Plan Transactions", permissions: "VIEW_PRO_PLAN_TRANSACTIONS" },
        { path: "/invoice", icon: <span className="material-symbols-outlined">receipt_long</span>, label: "Invoice", permissions: "VIEW_INVOICE" },
        { path: "/payment-gateway", icon: <span className="material-symbols-outlined">assured_workload</span>, label: "Payment Gateway", permissions: "VIEW_PAYMENT_GATEWAY" },
        { path: "/offers", icon: <span className="material-symbols-outlined">percent_discount</span>, label: "Offers", permissions: "VIEW_OFFERS" },
        { path: "/referral-program", icon: <span className="material-symbols-outlined">handshake</span>, label: "Referral Program" },
      ]
    },
    { path: "/broker-management", icon: <span className="material-symbols-outlined">account_balance</span>, label: "Broker Management", permissions: "VIEW_ACCESSCONTROL" },
    {
      label: "Notification",
      icon: <span className="material-symbols-outlined">notifications</span>,
      children: [
        {
          label: "Notification Query",
          icon: <span className="material-symbols-outlined">manage_search</span>,
          path: "/notification-query",
          permissions: "VIEW_NOTIFICATIONQUERY"
        },
        {
          label: "Notifications",
          icon: <span className="material-symbols-outlined">campaign</span>,
          path: "/notifications",
          permissions: "VIEW_NOTIFICATIONS"
        },
        {
          label: "Lead Campaigns",
          icon: <span className="material-symbols-outlined">ad</span>,
          path: "/lead-campaigns",
          permissions: "VIEW_NOTIFICATIONS"
        },
        {
          label: "Auto Notifications",
          icon: <span className="material-symbols-outlined">smart_toy</span>,
          path: "/auto-notifications",
          permissions: "VIEW_NOTIFICATIONS"
        },
        {
          label: "Email Notification",
          icon: <span className="material-symbols-outlined">mail</span>,
          path: "/email-notifications",
          permissions: "VIEW_EMAIL_NOTIFICATIONS"
        },
        {
          label: "Email Unsubscribes",
          icon: <span className="material-symbols-outlined">unsubscribe</span>,
          path: "/email-unsubscribes",
          permissions: "VIEW_EMAIL_NOTIFICATIONS"
        },
        {
          label: "SMS Notification",
          icon: <span className="material-symbols-outlined">sms</span>,
          path: "/sms-notifications",
          permissions: "VIEW_SMSNOTIFICATION"
        },

      ]
    },
    {
      icon: <span className="material-symbols-outlined">key</span>,
      label: "Access",
      children: [
        { path: "/access-roles", icon: <span className="material-symbols-outlined">key</span>, label: "Access Roles", permissions: "VIEW_ACCESSCONTROL" },
        { path: "/access-types", icon: <span className="material-symbols-outlined">admin_panel_settings</span>, label: "Access Types", permissions: "VIEW_ACCESSCONTROL" },
        { path: "/access-rights", icon: <span className="material-symbols-outlined">admin_panel_settings</span>, label: "Access Rights", permissions: "VIEW_ACCESSCONTROL" },
      ]
    },
    // { path: '/analytics', icon: '📋', label: 'Analytics' },
  ];

  // const quickActions = [
  //   { label: "New Post", icon: "✏️" },
  //   { label: "Add Stock", icon: "➕" },
  //   { label: "Create Alert", icon: "🔔" },
  // ];

  const handleDropdownClick = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const filteredMenuItems = menuItems
    .map(item => {
      // 1. Check parent-level permissions first
      if (item.permissions) {
        if (Array.isArray(item.permissions)) {
          if (!item.permissions.some(p => hasPermission(p))) return null;
        } else {
          if (!hasPermission(item.permissions)) return null;
        }
      }

      // 2. Then filter children if any exist
      if (item.children) {
        const filteredChildren = item.children.filter(
          child => {
            if (!child.permissions) return true; // Inherits parent permission since parent wasn't returned null above
            if (Array.isArray(child.permissions)) {
              return child.permissions.some(p => hasPermission(p));
            }
            return hasPermission(child.permissions);
          }
        );

        if (filteredChildren.length === 0) return null;

        return { ...item, children: filteredChildren };
      }

      return item;
    })
    .filter(Boolean);

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} />}
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}>
        <div className={styles.sidebarHeader}>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <nav className={styles.nav} ref={navRef}>
          <ul className={styles.navList}>
            {filteredMenuItems
              .map((item, index) => (
                <li
                  key={index}
                  className={styles.navItemContainer}
                  onMouseEnter={() => {
                    if (!window.matchMedia("(max-width: 1300px)").matches) {
                      if (timeoutRef.current) clearTimeout(timeoutRef.current);
                      setOpenDropdown(item.label);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!window.matchMedia("(max-width: 1300px)").matches) {
                      timeoutRef.current = setTimeout(() => {
                        setOpenDropdown(null);
                      }, 200);
                    }
                  }}
                >
                  {item.children ? (
                    <>
                      <button
                        className={`${styles.navLink} ${item.children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')) ? styles.active : ""}`}
                        onClick={() => window.matchMedia("(max-width: 1300px)").matches && handleDropdownClick(item.label)}
                      >
                        <span className={styles.navIcon}>{item.icon}</span>
                        <span className={styles.navLabel}>
                          {item.label}
                          {item.badge > 0 && (
                            <span className={styles.navBadge}>
                              {item.badge > 99 ? "99+" : item.badge}
                            </span>
                          )}
                          <span className={`${styles.chevron} ${openDropdown === item.label ? styles.chevronOpen : ""}`}>
                            <span className="material-symbols-outlined">keyboard_arrow_down</span>
                          </span>
                        </span>

                      </button>
                      {openDropdown === item.label && (
                        <div className={styles.dropdownMenu}>
                          {
                            item.children
                              .map((child) => (
                                  <Link
                                    key={child.path}
                                    to={child.path}
                                    className={styles.dropdownItem}
                                    onClick={() => { setOpenDropdown(null); onClose(); }}
                                  >
                                    <span className={styles.navIcon}>{child.icon}</span>
                                    <span>{child.label}</span>
                                    {child.badge > 0 && (
                                      <span className={styles.dropdownBadge}>
                                        {child.badge > 99 ? "99+" : child.badge}
                                      </span>
                                    )}
                                  </Link>
                              ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`${styles.navLink} ${location.pathname === item.path ? styles.active : ""}`}
                      onClick={onClose}
                    >
                      <span className={styles.navIcon}>{item.icon}</span>
                      <span className={styles.navLabel}>{item.label}</span>
                    </Link>
                  )}
                </li>
              ))}
            {/* Profile Icon Next to Links */}
            <li className={styles.navItemContainer} ref={profileRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 0' }}>
              <button
                className={styles.profileButton}
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                style={{ background: 'transparent', padding: '0', cursor: 'pointer', border: 'none' }}
              >
                <div className={styles.userAvatar}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>person</span>
                </div>
              </button>

              {isProfileOpen && (
                <div className={styles.profileDropdownMenu}>
                  <Link to="/profile" className={styles.dropdownItem} onClick={() => setIsProfileOpen(false)}>
                    👤 Profile
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button onClick={handleSignOut} className={styles.dropdownItem}>
                    🚪 Sign Out
                  </button>
                </div>
              )}
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
